/**
 * <s-gallery-dots data-track="ID"> — dot pagination for a horizontal scroll-snap gallery (mobile only).
 * Each child <button data-index> scrolls the track to that slide; the dot of the slide nearest
 * the track's left edge gets aria-current="true". The track is plain CSS scroll-snap, so it still
 * swipes without JavaScript.
 */
class SGalleryDots extends HTMLElement {
  connectedCallback() {
    this.track = document.getElementById(this.dataset.track);
    if (!this.track) return;
    this.dots = [...this.querySelectorAll('button[data-index]')];
    this.slides = [...this.track.children].filter((el) => el.matches('[data-slide]'));
    this.onClick = (event) => {
      const dot = event.target.closest('button[data-index]');
      if (!dot) return;
      const slide = this.slides[Number(dot.dataset.index)];
      if (!slide) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    };
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.onScroll());
    this.slides.forEach((slide) => this.resizeObserver.observe(slide));
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /** Mobile only: the track takes the height of the visible slide, so dots sit right under it even when photos have different proportions. */
  syncHeight(index) {
    const mobile = window.matchMedia('(max-width: 47.99rem)').matches;
    const slide = this.slides[index];
    if (!mobile || !slide) {
      this.track.style.height = '';
      return;
    }
    this.track.style.height = `${slide.offsetHeight}px`;
  }

  update() {
    if (!this.slides?.length) return;
    const start = this.slides[0].offsetLeft;
    const left = this.track.scrollLeft;
    let active = 0;
    let best = Infinity;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - start - left);
      if (distance < best) {
        best = distance;
        active = index;
      }
    });
    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
    this.syncHeight(active);
  }
}

if (!customElements.get('s-gallery-dots')) customElements.define('s-gallery-dots', SGalleryDots);
